import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Clock, Mail, Info, CircleCheck } from '../../assets/icons';
import '../../assets/css/doctor/register-pending.css';
import { doctorApi } from '../../network/apis/doctors';

const RegisterPending = () => {
    const navigate = useNavigate();
    const [applicationStatus, setApplicationStatus] = useState({
        status: 'pending',
        submissionDate: '',
        estimatedWaitTime: '48-72 hours',
        message: '',
        doctorData: null,
        loading: true,
        error: null
    });

    useEffect(() => {
        // Fetch the doctor application status
        const fetchApplicationStatus = async () => {
            try {
                const response = await doctorApi.getDoctorApplicationStatus();
                
                // If user doesn't have a pending application, redirect to registration
                if (response.data.status === 'not_found') {
                    navigate('/vet/register');
                    return;
                }

                setApplicationStatus({
                    status: response.data.status || 'pending',
                    submissionDate: response.data.submissionDate || new Date().toISOString(),
                    estimatedWaitTime: response.data.estimatedWaitTime || '48-72 hours',
                    message: response.data.message || '',
                    doctorData: response.data.doctorData || null,
                    loading: false,
                    error: null
                });
            } catch (error) {
                console.error('Error fetching application status:', error);
                setApplicationStatus(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Failed to retrieve your application status. Please try again later.'
                }));
            }
        };

        fetchApplicationStatus();
    }, [navigate]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Function to render appropriate status content
    const renderStatusContent = () => {
        switch (applicationStatus.status) {
            case 'approved':
                return (
                    <div className="status-approved">
                        <div className="status-icon">
                            <img src={CircleCheck} alt="Approved" />
                        </div>
                        <h2>Application Approved!</h2>
                        <p>
                            Congratulations! Your application has been approved. You can now access your veterinarian dashboard.
                        </p>
                        <div className="status-actions">
                            <Button 
                                text="Go to Dashboard"
                                variant="success"
                                onClick={() => navigate('/vet/dashboard')}
                            />
                        </div>
                    </div>
                );
                
            case 'rejected':
                return (
                    <div className="status-rejected">
                        <div className="status-icon rejected">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <h2>Application Not Approved</h2>
                        <p>
                            Unfortunately, your application has not been approved. Please see below for details:
                        </p>
                        {applicationStatus.message && (
                            <div className="rejection-reason">
                                <h3>Reason:</h3>
                                <p>{applicationStatus.message}</p>
                            </div>
                        )}
                        <div className="status-actions">
                            <Button 
                                text="Start New Application"
                                variant="primary"
                                onClick={() => navigate('/vet/register')}
                            />
                            <Button 
                                text="Contact Support"
                                variant="outline"
                                onClick={() => navigate('/contact')}
                            />
                        </div>
                    </div>
                );
                
            case 'pending':
            default:
                return (
                    <div className="status-pending">
                        <div className="status-icon pending">
                            <img src={Clock} alt="Pending" />
                        </div>
                        <h2>Application Under Review</h2>
                        <p>
                            Thank you for applying to join our network of aquatic health specialists! 
                            Your application is currently being reviewed by our team.
                        </p>
                        
                        <div className="status-details">
                            <div className="detail-item">
                                <label>Submission Date:</label>
                                <span>{formatDate(applicationStatus.submissionDate)}</span>
                            </div>
                            <div className="detail-item">
                                <label>Estimated Wait Time:</label>
                                <span>{applicationStatus.estimatedWaitTime}</span>
                            </div>
                        </div>

                        <div className="what-happens-next">
                            <h3>What happens next?</h3>
                            <ul>
                                <li>Our team will review your professional credentials</li>
                                <li>We may contact you for additional information</li>
                                <li>You'll receive an email notification once a decision is made</li>
                                <li>If approved, you'll get access to our veterinarian dashboard</li>
                            </ul>
                        </div>

                        <div className="contact-info">
                            <h3>Questions?</h3>
                            <p>
                                If you have any questions about your application, please contact us at{" "}
                                <a href="mailto:support@eaquahealth.com">support@eaquahealth.com</a>
                            </p>
                        </div>

                        <div className="status-actions">
                            <Button 
                                text="Check Email"
                                variant="outline"
                                icon={<img src={Mail} alt="Email" />}
                                onClick={() => window.open('https://mail.google.com', '_blank')}
                            />
                            <Button 
                                text="Contact Support"
                                variant="outline"
                                icon={<img src={Info} alt="Help" />}
                                onClick={() => navigate('/contact')}
                            />
                        </div>
                    </div>
                );
        }
    };

    if (applicationStatus.loading) {
        return (
            <div className="register-pending-page loading">
                <div className="loading-spinner"></div>
                <p>Loading your application status...</p>
            </div>
        );
    }

    if (applicationStatus.error) {
        return (
            <div className="register-pending-page error">
                <div className="error-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h2>Something went wrong</h2>
                <p>{applicationStatus.error}</p>
                <div className="error-actions">
                    <Button 
                        text="Try Again" 
                        variant="primary"
                        onClick={() => window.location.reload()}
                    />
                    <Button 
                        text="Go to Home" 
                        variant="outline"
                        onClick={() => navigate('/')}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="register-pending-page">
            <div className="pending-container">
                <div className="status-header">
                    <h1>Veterinarian Application Status</h1>
                </div>
                
                <div className="status-card">
                    {renderStatusContent()}
                </div>
                
                <div className="navigation-links">
                    <Link to="/">Return to Home</Link>
                </div>
            </div>
        </div>
    );
};

export default RegisterPending;